<template>
  <v-system-bar height="32" theme="dark" class="ga-2">
    <v-theme-provider :theme="$vuetify.theme.global.name">
      <AppProgressNotificationsMenu />

      <v-spacer></v-spacer>

      <span v-if="$config.public.APP_ENVIRONMENT" class="flex-grow-1 text-center">
        {{ $t('AppEnvironment') }}:&nbsp;
        <span class="font-weight-black">
          {{ environment }}
        </span>
      </span>

      <template v-if="isDev">
        <v-menu :close-on-content-click="false">
          <template #activator="{ props }">
            <v-btn icon="mdi-bug" v-bind="props" variant="text" size="30" rounded="rounded"></v-btn>
          </template>
          <MenuItemsList :items="debugMenuItems"></MenuItemsList>
        </v-menu>

        <!-- Toggle Debug Labels -->
        <v-btn
          v-if="debugLabelsInstalled"
          v-tooltip="'Debug Labels'"
          icon="mdi-code-block-tags"
          rounded="rounded"
          :active="showDebugLabels"
          size="30"
          variant="text"
          @click="showDebugLabels = !showDebugLabels"
        >
        </v-btn>
        <v-divider vertical thickness="2"></v-divider>
      </template>

      <template v-if="user">
        <!-- Color Mode -->
        <v-btn
          :icon="themeIcon"
          variant="text"
          size="30"
          rounded="rounded"
          :loading="themeLoading"
          @click="cycleTheme"
        />

        <!-- User Menu -->
        <v-menu location="bottom">
          <template #activator="{ props }">
            <v-btn v-bind="props" size="small" variant="text" class="text-none">
              <v-icon start>mdi-account-circle</v-icon>
              {{ user.name }}
              <v-icon end>mdi-menu-down</v-icon>
            </v-btn>
          </template>

          <MenuItemsList :items="userMenuItems" :v-list-props="{ density: 'compact', minWidth: 200 }" />
        </v-menu>
        <UserProfileDialog v-model="appStore.userProfileDialog" />

        <v-divider vertical thickness="2"></v-divider>
      </template>

      <!-- About -->
      <v-btn
        v-tooltip="{ location: 'bottom', text: $t('AppActionAbout') }"
        icon="mdi-information-outline"
        variant="text"
        size="30"
        rounded="rounded"
        @click="appStore.aboutDialog = true"
      />
      <AppAboutDialog v-model="appStore.aboutDialog" />

      <!-- Backend Services -->
      <BackendServicesStatusMenu />
    </v-theme-provider>
  </v-system-bar>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRuntimeConfig } from '#app'
import { useTheme } from 'vuetify'
import useUser from '~/composables/useUser'
import { ThemeEnum } from '@gridcon-energy/schaltungstool-backend'
import BackendServicesStatusMenu from '~/components/Common/BackendServicesStatusMenu.vue'
import { injectBackendClient } from '~/api/backendClients'
import MenuItemsList from '~/components/Common/MenuItemsList.vue'
import type { MenuItem } from '~/lib/list-items'
import { isDev } from '~/helper/nuxt'
import { useAppStore } from '~/stores/app'
import { debugLabelsInstalled, showDebugLabels } from '~/composables/DebugComponentNamesMixin'
import { Routes } from '~/_routes'
import UserProfileDialog from '~/components/Common/UserProfileDialog.vue'
import AppAboutDialog from '~/components/AppAboutDialog.vue'
import AppProgressNotificationsMenu from '~/components/Common/AppProgressNotificationsMenu.vue'
import { Progress } from '~/lib/notify'

export default defineComponent({
  name: 'AppSystemBar',
  components: {
    AppAboutDialog,
    AppProgressNotificationsMenu,
    UserProfileDialog,
    MenuItemsList,
    BackendServicesStatusMenu,
  },
  setup() {
    const config = useRuntimeConfig()
    const theme = useTheme()
    const { user, logout, updateUser } = useUser()
    const backend = injectBackendClient()
    const appStore = useAppStore()

    return {
      environment: config.public.APP_ENVIRONMENT,
      user,
      logout,
      updateUser,
      vuetifyTheme: theme,
      backend,
      appStore,
      showDebugLabels,
      debugLabelsInstalled,
    }
  },
  data() {
    return {
      themeLoading: false,
      isDev,
    }
  },
  computed: {
    debugMenuItems(): MenuItem[] {
      return [
        {
          label: 'trigger error request',
          onClick: async () => {
            try {
              await this.backend.app.triggerError()
            } catch (e) {
              this.$handleFetchError(e)
            }
          },
        },
        {
          label: 'raise exception request',
          onClick: async () => {
            try {
              await this.backend.app.raiseException()
            } catch (e) {
              this.$handleFetchError(e)
            }
          },
        },
        {
          label: 'simulate progress notification (5s)',
          onClick: () => this.simulateProgressNotification(),
        },
      ]
    },
    userMenuItems(): MenuItem[] {
      return [
        {
          label: this.$t('UserProfileTitle'),
          icon: 'mdi-account',
          divider: 'bottom',
          onClick: () => (this.appStore.userProfileDialog = true),
        },
        {
          label: this.$t('AppActionDashboard'),
          to: { name: Routes.index },
          icon: 'mdi-monitor-dashboard',
        },
        {
          label: this.$t('AppActionLogout'),
          color: 'error',
          icon: 'mdi-logout',
          onClick: () => this.logout(),
        },
      ]
    },
    currentThemeSetting(): ThemeEnum {
      return this.user?.theme ?? ThemeEnum.Auto
    },
    themeIcon() {
      if (this.currentThemeSetting === ThemeEnum.Auto) {
        return 'mdi-brightness-auto'
      }
      if (this.currentThemeSetting === ThemeEnum.Dark) {
        return 'mdi-weather-night'
      }
      return 'mdi-weather-sunny'
    },
  },
  methods: {
    usesDarkTheme(theme: ThemeEnum) {
      return (
        theme === ThemeEnum.Dark ||
        (theme === ThemeEnum.Auto && window.matchMedia('(prefers-color-scheme: dark)').matches)
      )
    },
    nextTheme(theme: ThemeEnum) {
      const themeCycle = [ThemeEnum.Auto, ThemeEnum.Light, ThemeEnum.Dark]
      const index = themeCycle.indexOf(theme)
      return themeCycle[(index + 1) % themeCycle.length]
    },
    async cycleTheme() {
      this.themeLoading = true
      const newTheme = this.nextTheme(this.currentThemeSetting)

      // Update backend
      const result = await this.updateUser({ theme: newTheme })
      this.themeLoading = false
      if (result) {
        // Update Vuetify theme
        this.vuetifyTheme.change(this.usesDarkTheme(result.theme ?? ThemeEnum.Auto) ? 'dark' : 'light')
      }
    },
    simulateProgressNotification() {
      const durationMs = 5000
      const tickMs = 250
      const total = durationMs / tickMs
      const id = `debug-progress-${Date.now()}`
      const label = 'Debug progress notification'
      const createdAt = new Date().toISOString()
      let current = 0

      Progress(id, label, `Step ${tickMs}`, 'running', current, total, 0, createdAt)

      const intervalId = window.setInterval(() => {
        current += 1

        const isCompleted = current >= total
        const normalizedCurrent = Math.min(current, total)
        const percent = Math.round((normalizedCurrent / total) * 100)

        Progress(
          id,
          label,
          `Step ${tickMs * current}`,
          isCompleted ? 'completed' : 'running',
          normalizedCurrent,
          total,
          percent,
          createdAt,
          isCompleted ? new Date().toISOString() : null,
        )

        if (isCompleted) {
          window.clearInterval(intervalId)
        }
      }, tickMs)
    },
  },
})
</script>

<style scoped></style>
